import React, { FC, useState } from 'react'
import {
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material'
import { DateRange } from '@mui/x-date-pickers-pro'
import { startOfDay, endOfDay, formatISO } from 'date-fns'
import { useDialog } from 'src/hooks/ui'
import { useDownloadMeasurements } from 'src/hooks/api'
import { DialogNames } from 'src/enums'
import { StatefulDialog, DateRangePresetPicker, getDateRangePresetValue } from 'src/components/ui'

type DeviceType = 'sensor' | 'dataLogger' | 'wagnerProbe'

interface Props {
  deviceType: DeviceType
  deviceId: string
  hideTriggerButton?: boolean
}

export const DownloadMeasurementsModal: FC<Props> = ({ deviceType, deviceId, hideTriggerButton = false }) => {
  const { download, isLoading } = useDownloadMeasurements(deviceType, deviceId)
  const [dateRange, setDateRange] = useState<DateRange<Date>>(getDateRangePresetValue('today'))
  const [dateFrom, dateTo] = dateRange
  const [unit, setUnit] = useState<'F' | 'C'>('F')
  const dialogName = DialogNames.DownloadMeasurements
  const { openDialog, closeDialog } = useDialog(dialogName)

  const handleDownload = async () => {
    const { isError } = await download({
      dateFrom: dateFrom ? formatISO(startOfDay(dateFrom)) : undefined,
      dateTo: dateTo ? formatISO(endOfDay(dateTo)) : undefined,
      unit,
    })
    if (!isError) closeDialog()
  }

  return (
    <Grid>
      {hideTriggerButton ? null : (
        <Button variant="outlined" size="large" onClick={() => openDialog()} sx={{ width: 260 }}>
          Download measurements
        </Button>
      )}
      <StatefulDialog
        name={dialogName}
        onClose={closeDialog}
        actions={
          <DialogActions>
            <Button variant="outlined" onClick={() => closeDialog()}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleDownload} disabled={isLoading || !dateFrom || !dateTo}>
              {isLoading ? <CircularProgress size={22} /> : 'Download'}
            </Button>
          </DialogActions>
        }
      >
        <DialogTitle>Download measurements</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a date range and temperature unit
          </DialogContentText>
          <DateRangePresetPicker value={dateRange} onChange={setDateRange} />
          <Grid container alignItems="center" spacing={1} mt={2}>
            <Grid item>
              <Typography variant="body2">Temperature unit:</Typography>
            </Grid>
            <Grid item>
              <ToggleButtonGroup
                color="primary"
                value={unit}
                exclusive
                onChange={(_, value) => value && setUnit(value)}
                size="small"
              >
                <ToggleButton value="F">°F</ToggleButton>
                <ToggleButton value="C">°C</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </DialogContent>
      </StatefulDialog>
    </Grid>
  )
}
